import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const YearButton = React.memo(({ year, isSelected, onClick }) => (
  <button
    onClick={onClick}
    aria-pressed={isSelected}
    className={`px-4 py-2 rounded-full text-sm transition-colors ${
      isSelected
        ? "bg-gray-700 text-gray-100"
        : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-100 cursor-pointer"
    }`}
  >
    {year}
  </button>
));

YearButton.displayName = "YearButton";

YearButton.propTypes = {
  year: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

function History({ historyImage, selectedYear, setSelectedYear }) {
  const { t } = useTranslation();
  const intro = t("components.aboutUs.history.intro", { returnObjects: true });
  const historyYears = t("components.aboutUs.history.years", {
    returnObjects: true,
  });
  const imageData = t("components.aboutUs.history.image", {
    returnObjects: true,
  });

  const yearKeys = useMemo(() => Object.keys(historyYears), [historyYears]);

  return (
    <section className="relative flex items-center justify-center bg-black pb-12 pt-12">
      <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-gray-900 to-black"></div>

      <div className="relative z-10 flex flex-col gap-8 px-4 md:px-6 lg:flex-row lg:items-center lg:justify-between lg:gap-12 bg-black text-gray-100">
        <div className="max-w-lg">
          <h1 className="text-2xl lg:text-3xl font-medium mb-4 text-gray-100">
            {intro.title}
          </h1>
          <p className="text-md lg:text-lg mb-6 text-gray-400">
            {intro.description}
          </p>
          <a
            href={intro.buttonLink}
            className="inline-block bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white px-6 py-3 rounded-lg transition-colors"
            role="button"
          >
            {intro.buttonText}
          </a>
        </div>

        <div className="flex-1 order-3 lg:order-2 lg:block">
          {typeof window !== "undefined" && window.innerWidth >= 1024 && (
            <img
              src={historyImage}
              alt={imageData.alt || "Solar panel installation"}
              className="rounded-3xl w-full max-w-[500px] h-[400px] object-cover mx-auto"
              loading="lazy"
            />
          )}
        </div>

        <div className="max-w-md order-2 lg:order-3">
          <div className="flex flex-wrap gap-3 mb-8" role="tablist">
            {yearKeys.map((year) => (
              <YearButton
                key={year}
                year={year}
                isSelected={selectedYear === parseInt(year)}
                onClick={() => setSelectedYear(parseInt(year))}
              />
            ))}
          </div>
          <h2 className="text-xl lg:text-2xl font-medium mb-4 text-gray-100">
            {historyYears[selectedYear].title}
          </h2>
          <p className="text-md lg:text-lg text-gray-400 leading-relaxed">
            {historyYears[selectedYear].description}
          </p>
        </div>
      </div>
    </section>
  );
}

History.propTypes = {
  historyImage: PropTypes.string.isRequired,
  selectedYear: PropTypes.number.isRequired,
  setSelectedYear: PropTypes.func.isRequired,
};

export default React.memo(History);
